import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { AlertTriangle, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { newSelect as Select } from '../ui/newSelect';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';
import { SUPPORTED_DATE_FORMATS } from '../../constants/importConstants';

const DateFormatWarning = ({
  importId,
  job,
  orgDateFormat,
  setDateFormat,
  continueImport,
  getOrgDateFormat,
  setOrgDefaultDateFormat,
}) => {
  const [selectedFormat, setSelectedFormat] = useState(job?.date_format || '');
  const [submitting, setSubmitting] = useState(false);
  const [updateOrgDefault, setUpdateOrgDefault] = useState(false);

  useEffect(() => {
    getOrgDateFormat();
  }, []);

  const dateFormatOptions = SUPPORTED_DATE_FORMATS.map((f) => ({
    value: f.pattern,
    label: `${f.pattern} (e.g. ${f.example})`,
  }));

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    try {
      if (selectedFormat !== job?.date_format) {
        await setDateFormat(importId, selectedFormat);
      }
      if (updateOrgDefault && selectedFormat) {
        await setOrgDefaultDateFormat(selectedFormat);
      }
      await continueImport(importId);
    } finally {
      setSubmitting(false);
    }
  }, [selectedFormat, importId, job, setDateFormat, continueImport, updateOrgDefault, setOrgDefaultDateFormat]);

  return (
    <div className='flex flex-col gap-4'>
      <Alert className='border-warning bg-warning-bg'>
        <AlertTriangle size={16} className='text-warning' />
        <AlertDescription className='text-warning'>
          The date format was auto-detected with low confidence. Please verify it&apos;s correct before proceeding.
        </AlertDescription>
      </Alert>

      <Card className='bg-card'>
        <CardHeader>
          <CardTitle className='text-lg flex items-center gap-2'>
            <Calendar size={20} strokeWidth={1.5} />
            Date Format
          </CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium text-foreground mb-1.5 block'>Detected Format</label>
              <div className='p-3 bg-muted rounded-md text-sm font-mono'>
                {job?.date_format || 'Unknown'}
                {job?.date_format_confidence && (
                  <span className='ml-2 text-xs text-warning font-sans'>({job.date_format_confidence})</span>
                )}
              </div>
            </div>
            <div>
              <label className='text-sm font-medium text-foreground mb-1.5 block'>Org Default</label>
              <div className='p-3 bg-muted rounded-md text-sm font-mono'>
                {orgDateFormat?.defaultDateFormat || 'Not set'}
              </div>
            </div>
          </div>

          <div>
            <label className='text-sm font-medium text-foreground mb-1.5 block'>Correct Date Format</label>
            <Select
              value={selectedFormat}
              onValueChange={setSelectedFormat}
              options={dateFormatOptions}
              placeholder='Select date format'
            />
          </div>

          {job?.sampleDates?.length > 0 && (
            <div>
              <label className='text-sm font-medium text-muted-foreground mb-1 block'>Sample dates from file</label>
              <div className='flex gap-2 flex-wrap'>
                {job.sampleDates.map((d, i) => (
                  <span key={i} className='px-2 py-1 bg-muted rounded text-sm font-mono'>{d}</span>
                ))}
              </div>
            </div>
          )}

          <label className='flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              checked={updateOrgDefault}
              onChange={(e) => setUpdateOrgDefault(e.target.checked)}
              className='rounded border-border'
            />
            <span className='text-muted-foreground'>Set this as the organisation&apos;s default date format</span>
          </label>

          <div className='flex justify-end gap-3'>
            <Button variant='outline' onClick={() => continueImport(importId)} disabled={submitting}>
              Use Detected Format
            </Button>
            <Button onClick={handleConfirm} disabled={!selectedFormat || submitting} loading={submitting}>
              Confirm & Continue
              <ArrowRight size={16} strokeWidth={1.5} />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  orgDateFormat: state.intelligentImport?.orgDateFormat,
});

const mapDispatchToProps = {
  setDateFormat: IMPORT_ACTIONS.setDateFormat,
  continueImport: IMPORT_ACTIONS.continueImport,
  getOrgDateFormat: IMPORT_ACTIONS.getOrgDateFormat,
  setOrgDefaultDateFormat: IMPORT_ACTIONS.setOrgDefaultDateFormat,
};

export default connect(mapStateToProps, mapDispatchToProps)(DateFormatWarning);
